import { Component, inject, OnInit } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../../services/auth.service';
import { take } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-auth',
  templateUrl: './auth-v2.component.html',
  standalone: true,
  imports: [FormsModule, RouterModule]
})
export class AuthComponent implements OnInit {
  private authService = inject(AuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  isSignIn = true;
  email = '';
  password = '';
  rememberMe = true;
  emailError: string | null = null;
  passwordError: string | null = null;
  bannerError: string | null = null;
  isLoading = false;
  returnUrl: string = '/';

  // Password validation properties
  passwordValidation = {
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecialChar: false
  };
  showPasswordRequirements = false;

  /** When false, "Continue with Google" is hidden (work-email sign-in only). Controlled by environment. */
  enableGoogleSignIn = environment.enableGoogleSignIn ?? false;

  ngOnInit() {
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
    // `?mode=signup` (v3 navbar "Sign Up") opens in create-account mode.
    if (this.route.snapshot.queryParams['mode'] === 'signup') {
      this.isSignIn = false;
    }
    this.loadGeistFont();
    this.loadRememberedEmail();
    
    // If user is already logged in, redirect to returnUrl
    this.checkAuthAndRedirect();
  }

  private checkAuthAndRedirect() {
    // resolvedUser$ so a signed-in user who reloads /auth is redirected (BUG-6).
    this.authService.resolvedUser$.pipe(take(1)).subscribe(user => {
      if (user) {
        // User is already logged in, redirect to returnUrl
        this.navigateToReturnUrl();
      }
    });
  }

  private navigateToReturnUrl() {
    // Parse the returnUrl to handle query parameters correctly
    try {
      const url = new URL(this.returnUrl, window.location.origin);
      const path = url.pathname;
      const queryParams: { [key: string]: string } = {};
      
      url.searchParams.forEach((value, key) => {
        queryParams[key] = value;
      });

      if (Object.keys(queryParams).length > 0) {
        this.router.navigate([path], { queryParams });
      } else {
        this.router.navigate([path]);
      }
    } catch (e) {
      // Fallback to simple navigation
      this.router.navigate([this.returnUrl]);
    }
  }

  private loadRememberedEmail() {
    if (typeof localStorage !== 'undefined') {
      const rememberedEmail = localStorage.getItem('rememberedEmail');
      if (rememberedEmail) {
        this.email = rememberedEmail;
        this.rememberMe = true;
      }
    }
  }

  private saveRememberedEmail() {
    if (typeof localStorage !== 'undefined') {
      if (this.rememberMe) {
        localStorage.setItem('rememberedEmail', this.email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }
    }
  }

  private loadGeistFont() {
    if (typeof document !== 'undefined') {
      const existingLink = document.getElementById('geist-font-link');
      if (!existingLink) {
        const link = document.createElement('link');
        link.id = 'geist-font-link';
        link.rel = 'stylesheet';
        link.href = 'https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700&display=swap';
        document.head.appendChild(link);
      }
    }
  }

  toggleView() {
    this.isSignIn = !this.isSignIn;
    this.clearErrors();
    this.showPasswordRequirements = false;
    if (this.password) {
      this.onPasswordChange();
    }
  }

  private clearErrors() {
    this.emailError = null;
    this.passwordError = null;
    this.bannerError = null;
  }

  onPasswordChange() {
    this.validatePassword();
    this.showPasswordRequirements = !this.isSignIn && this.password.length > 0;
  }

  private validatePassword() {
    const password = this.password;
    this.passwordValidation.minLength = password.length >= 8;
    this.passwordValidation.hasUppercase = /[A-Z]/.test(password);
    this.passwordValidation.hasLowercase = /[a-z]/.test(password);
    this.passwordValidation.hasNumber = /[0-9]/.test(password);
    this.passwordValidation.hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
  }

  get isPasswordValid(): boolean {
    return this.passwordValidation.minLength &&
           this.passwordValidation.hasUppercase &&
           this.passwordValidation.hasLowercase &&
           this.passwordValidation.hasNumber &&
           this.passwordValidation.hasSpecialChar;
  }

  onSubmit() {
    if (this.isSignIn) {
      this.handleEmailSignIn();
    } else {
      this.handleCreateUser();
    }
  }

  async handleEmailSignIn() {
    if (!this.email || !this.password) {
      this.bannerError = "Email and password are required.";
      return;
    }
    this.isLoading = true;
    this.clearErrors();
    try {
      await this.authService.emailSignIn(this.email, this.password, this.rememberMe);
      this.saveRememberedEmail();
      this.navigateToReturnUrl();
    } catch (error: any) {
      this.categorizeAuthError(error);
    } finally {
      this.isLoading = false;
    }
  }

  async handleCreateUser() {
    if (!this.email || !this.password) {
      this.bannerError = "Email and password are required for account creation.";
      return;
    }

    if (!this.isPasswordValid) {
      this.passwordError = "Password must meet all requirements listed below.";
      this.showPasswordRequirements = true;
      return;
    }

    this.isLoading = true;
    this.clearErrors();
    try {
      await this.authService.createUserWithEmail(this.email, this.password);
      await this.authService.sendEmailVerification();
      await this.router.navigate(['/email-verification']);
    } catch (error: any) {
      this.categorizeAuthError(error);
    } finally {
      this.isLoading = false;
    }
  }

  async handleGoogleSignIn() {
    this.isLoading = true;
    this.clearErrors();
    try {
      const result = await this.authService.googleSignIn(this.rememberMe);
      if (result?.user?.email && this.rememberMe) {
        this.email = result.user.email;
        this.saveRememberedEmail();
      }
      this.navigateToReturnUrl();
    } catch (error: any) {
      this.categorizeAuthError(error);
    } finally {
      this.isLoading = false;
    }
  }

  private categorizeAuthError(error: any): void {
    if (error.code) {
      switch (error.code) {
        case 'auth/invalid-email':
          this.emailError = 'Invalid email format.';
          break;
        case 'auth/user-not-found':
          this.emailError = 'No account found with this email address.';
          break;
        case 'auth/wrong-password':
          this.passwordError = 'Incorrect password.';
          break;
        case 'auth/invalid-credential':
          this.bannerError = 'Invalid email or password. Please check your credentials.';
          break;
        case 'auth/email-already-in-use':
          this.emailError = 'This email is already registered.';
          break;
        case 'auth/weak-password':
          this.passwordError = 'Password is too weak. It should be at least 6 characters.';
          break;
        case 'auth/requires-recent-login':
          this.bannerError = 'This operation requires recent authentication. Please log in again.';
          break;
        case 'auth/popup-closed-by-user':
          this.bannerError = 'Sign-in popup closed. Please try again.';
          break;
        case 'auth/cancelled-popup-request':
          this.bannerError = 'Multiple sign-in attempts detected. Please complete one or try again.';
          break;
        default:
          console.error("Firebase Auth Error Code:", error.code, "Message:", error.message);
          this.bannerError = error.message || 'An unexpected error occurred. Please try again.';
      }
    } else {
      console.error("Firebase Auth Error (no code):", error);
      this.bannerError = error.message || 'An unexpected error occurred. Please try again.';
    }
  }
}

